import {
  Folder,
  RenewalPointer,
} from "../../redux/interfaces/folder.types.ts";
import { hasLeaseContract } from "./hasLeaseContract.ts";

/**
 * La renovación ACTIVA que ya se creó a partir de este expediente, o `null`.
 *
 * El backend no guarda este dato en el expediente de origen: lo calcula al servir el
 * DETALLE, con la misma consulta inversa que usa su guardia de idempotencia ("qué folio
 * activo me declara como origen"). Por eso `renewed_into` es fiable en el detalle y una
 * renovación desactivada deja de aparecer en cuanto se recarga el expediente.
 *
 * Sólo existe en el detalle. El listado no lo calcula —serían N consultas por página—, así
 * que esta función no debe usarse con un folio que venga del listado: ahí su ausencia NO
 * significa "no hay renovación". El botón sólo se pinta en la pantalla del expediente, que
 * es la que trae el detalle.
 *
 * Aun así el 409 del backend sigue siendo la última palabra: entre la carga de la pantalla
 * y el clic alguien más pudo renovar.
 */
export const getExistingRenewal = (folder: Folder): RenewalPointer | null => {
  const renewedInto = folder.metadata?.renewal?.renewed_into;
  if (!renewedInto) return null;
  // Un objeto vacío sería un puntero sin destino: no oculta nada.
  return renewedInto.id || renewedInto.folder_number ? renewedInto : null;
};

/**
 * MB-379 — por qué NO se puede renovar este expediente, o `null` si se puede.
 *
 * Devuelve el PRIMER motivo, en el mismo orden en que los comprueba el backend
 * (`docvault.renewals.projection._assert_renewable`), para que el tooltip del
 * botón deshabilitado diga lo mismo que diría el 4xx si se llegara a pedir:
 *
 * 1. Expediente desactivado (RENEWAL_SOURCE_INACTIVE). Se renueva siempre el
 *    vigente: renovar uno desactivado bifurcaría la cadena.
 * 2. Sin número de folio (RENEWAL_SOURCE_WITHOUT_FOLIO).
 * 3. Sin plantilla de contrato (RENEWAL_WITHOUT_CONTRACT_TEMPLATE). Es la MISMA
 *    condición con la que el backend decide si asigna abogado: sin plantilla no
 *    hay clausulado que heredar. `RenewFolder` no pinta el botón en este caso
 *    —una investigación no tiene renovación pendiente—, pero el motivo se queda
 *    aquí para que la función siga siendo el espejo completo del backend.
 * 4. Contrato no generado (RENEWAL_WITHOUT_GENERATED_CONTRACT). Se mira sólo
 *    `is_lease_contract_generated` —la bandera que pinta "Contrato Generado el
 *    ..." en la tarjeta—, ni la fecha ni el estado de firma: el backend la
 *    enciende al entregar el contrato y no la apaga al firmarlo ni al cancelarlo.
 * 5. Contrato cancelado (RENEWAL_CONTRACT_CANCELLED). Cancelar no apaga la
 *    bandera de generado, pero un contrato cancelado no tiene nada que renovar.
 *
 * Que ya exista una renovación NO es un motivo de bloqueo aquí: ese caso se
 * resuelve antes, con `getExistingRenewal`, y en lugar del botón deshabilitado
 * se ofrece el enlace "Ver la renovación".
 */
export const renewalBlockReason = (folder: Folder): string | null => {
  // Sólo bloquea un `false` explícito (`!== false`, no `=== true`): un payload
  // viejo sin la clave no debe esconder la acción; el backend tiene la última palabra.
  if (folder.is_active === false) {
    return "El expediente está desactivado. Se renueva siempre el expediente vigente.";
  }
  // `== null` cubre `null` y la clave ausente, igual que el `is None` del backend.
  if (folder.folder_number == null) {
    return "El expediente no tiene número de folio.";
  }
  if (!hasLeaseContract(folder)) {
    return "El expediente no tiene plantilla de contrato.";
  }
  // `=== true` y no `!== false`, al revés que `is_active`: aquí la clave ausente
  // significa "no consta contrato generado", y en la duda no se ofrece renovar.
  if (folder.is_lease_contract_generated !== true) {
    return "Aún no se ha generado el contrato de este expediente.";
  }
  if (folder.lease_contract_signing_status === "cancelled") {
    return "El contrato de este expediente fue cancelado.";
  }
  return null;
};

/**
 * Lo que el botón de renovación sabe de esta visita y el expediente no dice:
 *
 * - `foundRenewal`: el puntero que trajo un 409. El 409 es la palabra definitiva
 *   sobre "ya existe una renovación" —el backend hace la consulta inversa que el
 *   cliente no puede hacer—, y la metadata que la delata puede estar desfasada.
 * - `inFlightFolio`: el folio de una renovación que sigue en vuelo pero que se
 *   pidió en OTRA visita. La petición es de un solo vuelo por pantalla: aquí un
 *   segundo "Crear" no haría nada, y el modal enseñaría el progreso ajeno.
 */
interface RenewalSessionState {
  foundRenewal?: RenewalPointer | null;
  inFlightFolio?: number | string | null;
}

/**
 * Qué pinta el botón de renovación:
 *
 * - `hidden`: nada. Renovar no aplica (sin plantilla de contrato) o ya hay
 *   renovación pero no se sabe a dónde llevar.
 * - `renewed`: el enlace "Ver la renovación". Es navegación, no la acción.
 * - `blocked`: el botón deshabilitado, con el motivo en un tooltip enfocable.
 * - `renewable`: el botón que abre la confirmación.
 */
type RenewalButtonState =
  | { kind: "hidden" }
  | { kind: "renewed"; id: string; folderNumber?: number | string }
  | { kind: "blocked"; reason: string }
  | { kind: "renewable" };

export const RENEWAL_ALREADY_FOUND_REASON =
  "Este expediente ya tiene una renovación.";

/**
 * MB-379 — la única regla que decide qué pinta `RenewFolder`.
 *
 * El orden importa: una renovación que ya existe se ofrece aunque el expediente
 * ya no cumpla lo que pedía renovarlo (p. ej. sin la bandera de contrato
 * generado), porque llevar a ella es navegar, no crear. Un expediente sin
 * plantilla de contrato no muestra nada: es una investigación, renovarla no es
 * algo que le falte. El 409 sigue siendo la última palabra por si alguien renovó
 * entre la carga y el clic: por eso entra aquí como `session.foundRenewal`.
 */
export const renewalButtonState = (
  folder: Folder,
  session: RenewalSessionState = {},
): RenewalButtonState => {
  const existing = getExistingRenewal(folder);
  if (existing) {
    return existing.id
      ? {
          kind: "renewed",
          id: existing.id,
          folderNumber: existing.folder_number,
        }
      : { kind: "hidden" };
  }

  const found = session.foundRenewal;
  if (found) {
    // Un 409 sin puntero sigue diciendo "ya existe": se explica, no se esconde.
    return found.id
      ? { kind: "renewed", id: found.id, folderNumber: found.folder_number }
      : { kind: "blocked", reason: RENEWAL_ALREADY_FOUND_REASON };
  }

  if (!hasLeaseContract(folder)) return { kind: "hidden" };

  const reason = renewalBlockReason(folder);
  if (reason) return { kind: "blocked", reason };

  if (session.inFlightFolio != null) {
    return {
      kind: "blocked",
      reason: `Hay una renovación en curso del expediente #${session.inFlightFolio}. Espera a que termine.`,
    };
  }

  return { kind: "renewable" };
};
